"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Tooltip,
  Alert,
  Popover,
} from "@mui/material"
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  FilterAlt as FilterAltIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  RestartAlt as RestartAltIcon,
  Assessment as AssessmentIcon,
  Image as ImageIcon,
  Videocam as VideocamIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
} from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import {
  getAdminCampaigns,
  getUserCampaigns,
  updateCampaignStatus,
  getCampaignById,
  resetCampaignState,
  updateCampaign,
  getCampaignStats,
} from "../slices/campaignSlice"
import { API_URL, MAX_CAMPAIGN_MESSAGE_LENGTH } from "../constants"
import CampaignDetailsDialog from "../components/campaigns/CampaignDetailsDialog"
import CampaignReportDialog from "../components/campaigns/CampaignReportDialog"

// Add CSS for stable loading state and running campaign animation
const tableStyles = `
  .table-loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.4); /* More transparent background */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    transition: opacity 0.3s ease;
    opacity: 1;
    pointer-events: none; /* Allow clicking through the overlay */
  }
  
  .table-container {
    position: relative;
    min-height: 300px;
  }
  
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      opacity: 1;
    }
  }
  
  .running-campaign {
    animation: pulse 2s infinite ease-in-out;
  }
`

const PendingCampaignPage = () => {
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth)
  const {
    campaigns,
    loading,
    error,
    success,
    campaign: campaignDetails,
    pagination,
    stats,
    isSubmitting,
  } = useSelector((state) => state.campaigns)

  const [searchQuery, setSearchQuery] = useState("")
  const [actionDialogOpen, setActionDialogOpen] = useState(false)
  const [actionType, setActionType] = useState("")
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [rejectionReason, setRejectionReason] = useState("")
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedCampaignId, setSelectedCampaignId] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editFormData, setEditFormData] = useState({
    title: "",
    message: "",
    buttons: [],
    numbers: [],
  })
  const [viewModeOnly, setViewModeOnly] = useState(false)
  const [isNonWhatsappFiltering, setIsNonWhatsappFiltering] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [media, setMedia] = useState([])
  const mediaInputRef = useRef(null)
  const refreshIntervalRef = useRef(null)
  const [hasFilteringInProgress, setHasFilteringInProgress] = useState(false)
  const fetchInProgress = useRef(false)

  // New state for server selection
  const [filterDialogOpen, setFilterDialogOpen] = useState(false)
  const [servers, setServers] = useState([])
  const [selectedServerId, setSelectedServerId] = useState("")
  const [loadingServers, setLoadingServers] = useState(false)
  const [delayedLoading, setDelayedLoading] = useState(false)
  const [openingModal, setopeningModal] = useState(false)

  // New state for process timeline
  const [showProcessTimeline, setShowProcessTimeline] = useState(false)
  const [processingCampaign, setProcessingCampaign] = useState(null)
  const [activeProcessStep, setActiveProcessStep] = useState(0)

  // New state for Find non-WhatsApp server selection
  const [findNonWhatsAppDialogOpen, setFindNonWhatsAppDialogOpen] = useState(false)
  const [findNonWhatsAppServerId, setFindNonWhatsAppServerId] = useState("")
  const [currentStepAction, setCurrentStepAction] = useState("")

  // State for validation status and error handling
  const [validationStatus, setValidationStatus] = useState(null)
  const [validationError, setValidationError] = useState(null)
  const [retryDialogOpen, setRetryDialogOpen] = useState(false)
  const [failedCampaignId, setFailedCampaignId] = useState(null)

  // Add a new ref for running campaigns polling
  const runningCampaignsIntervalRef = useRef(null)

  // Add a new state to track if there are running campaigns
  const [hasRunningCampaigns, setHasRunningCampaigns] = useState(false)

  // Date filter states for each tab
  const [dateFilters, setDateFilters] = useState({
    pending: { type: "today", date: null },
    processing: { type: "today", date: null },
    finished: { type: "today", date: null },
    rejected: { type: "today", date: null },
  })
  // Country Type filter
  const [countryFilter, setCountryFilter] = useState("all")
  const [reportChnagedFilter, setReportChnagedFilter] = useState("all")
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0])

  const [recordsPerPage, setRecordsPerPage] = useState(100)

  // New ref for general polling
  const generalPollingIntervalRef = useRef(null)

  // NEW: State for Campaign Report Dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [selectedCampaignForReport, setSelectedCampaignForReport] = useState(null)

  // State variables for statusFilter and page
  const [statusFilter, setStatusFilter] = useState("pending")
  const [page, setPage] = useState(1)

  // State for Send Message dialog
  const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false)
  const [sendMessageMobile, setSendMessageMobile] = useState("")
  const [selectedCampaignForSend, setSelectedCampaignForSend] = useState(null)

  const [resetConfirmDialogOpen, setResetConfirmDialogOpen] = useState(false)
  const [campaignToReset, setCampaignToReset] = useState(null)
  const [resetFromTab, setResetFromTab] = useState("")

  const [dataStatusKey, setDataStatusKey] = useState("pending")

  // Memoize filtered campaigns to prevent unnecessary recalculations
  const memoizedCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      const query = searchQuery?.toLowerCase() || ""

      return (
        campaign?.title?.toLowerCase().includes(query) ||
        campaign?.message?.toLowerCase().includes(query) ||
        campaign?.totalNumbers?.toString().includes(query) ||
        campaign?.countryCode?.toLowerCase().includes(query) ||
        campaign?.userId?.name?.toLowerCase().includes(query)
      )
    })
  }, [campaigns, searchQuery])

  // Silent refresh function that doesn't trigger loading state
  const silentlyRefreshCampaigns = useCallback(() => {
    if (fetchInProgress.current) return

    fetchInProgress.current = true

    // Prepare date filter parameter
    let dateFilterParam = null
    if (dateFilters[statusFilter].type === "today") {
      dateFilterParam = "today"
    } else if (dateFilters[statusFilter].type === "custom" && dateFilters[statusFilter].date) {
      dateFilterParam = dateFilters[statusFilter].date
    }

    const fetchOptions = {
      status: statusFilter,
      page,
      limit: recordsPerPage,
    }

    // Add dateFilter if it exists
    if (dateFilterParam) {
      fetchOptions.dateFilter = dateFilterParam
    }
    fetchOptions.countryFilter = countryFilter
    fetchOptions.reportChnagedFilter = reportChnagedFilter

    const promise =
      userInfo.user.role === "admin"
        ? dispatch(getAdminCampaigns(fetchOptions))
        : dispatch(getUserCampaigns(fetchOptions))

    promise
      .unwrap()
      .then(() => {
        setDataStatusKey(statusFilter)
        setTimeout(() => {
          fetchInProgress.current = false
        }, 100)
      })
      .catch(() => {
        fetchInProgress.current = false
      })
  }, [dispatch, userInfo, statusFilter, page, dateFilters, countryFilter, reportChnagedFilter, recordsPerPage])

  // Add this new function to fetch campaign stats
  const fetchCampaignStats = useCallback(() => {

    dispatch(getCampaignStats({ dateFilter: 'today' }))
      .unwrap()
      .catch(() => { })
  }, [dispatch])

  const fetchCampaigns = useCallback(() => {
    if (fetchInProgress.current) return

    fetchInProgress.current = true

    // Prepare date filter parameter
    let dateFilterParam = null
    if (dateFilters[statusFilter].type === "today") {
      dateFilterParam = "today"
    } else if (dateFilters[statusFilter].type === "custom" && dateFilters[statusFilter].date) {
      dateFilterParam = dateFilters[statusFilter].date
    }

    const fetchOptions = {
      status: statusFilter,
      page,
      limit: recordsPerPage,
    }

    // Add dateFilter if it exists
    if (dateFilterParam) {
      fetchOptions.dateFilter = dateFilterParam
    }
    fetchOptions.countryFilter = countryFilter
    fetchOptions.reportChnagedFilter = reportChnagedFilter

    const promise =
      userInfo.user.role === "admin"
        ? dispatch(getAdminCampaigns(fetchOptions))
        : dispatch(getUserCampaigns(fetchOptions))

    promise
      .unwrap()
      .then(() => {
        setDataStatusKey(statusFilter)
        // Add a small delay before marking fetch as complete
        setTimeout(() => {
          fetchInProgress.current = false
        }, 100)
      })
      .catch(() => {
        fetchInProgress.current = false
      })
  }, [dispatch, userInfo, statusFilter, page, dateFilters, countryFilter, reportChnagedFilter, recordsPerPage])

  // Initial fetch of campaigns and stats with debouncing
  useEffect(() => {
    fetchCampaigns()
    fetchCampaignStats()
  }, [page, statusFilter, dateFilters, countryFilter, reportChnagedFilter, fetchCampaigns, fetchCampaignStats])

  // Only show loading indicator for operations that take longer than 500ms
  useEffect(() => {
    let timer
    if (loading) {
      // Only set loading state after a delay
      timer = setTimeout(() => {
        setDelayedLoading(true)
      }, 500) // Only show loader if operation takes more than 500ms
    } else {
      setDelayedLoading(false)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [loading])

  // Handle success and error states - Updated to handle tab switching after rejection
  useEffect(() => {
    if (success) {
      //toast.success("Campaign status updated successfully")

      // If we just rejected a campaign, switch to rejected tab
      if (actionType === "reject") {
        setStatusFilter("rejected")
        setPage(1) // Reset to first page
      }

      setActionDialogOpen(false)
      setRejectionReason("")
      setActionType("") // Reset action type
      fetchCampaigns()
      fetchCampaignStats() // Refresh stats to update tab counts
      dispatch(resetCampaignState())
    }
  }, [success, dispatch, actionType, fetchCampaigns, fetchCampaignStats])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetCampaignState())
    }
  }, [error, dispatch])

  useEffect(() => {
    if (campaignDetails) {
      setEditFormData({
        title: campaignDetails.title || "",
        message: campaignDetails.message || "",
        buttons: campaignDetails.buttons || [],
        numbers: campaignDetails.numbers || [],
      })
      setMedia(campaignDetails.media || [])
    }
  }, [campaignDetails])

  useEffect(() => {
    if (showProcessTimeline && processingCampaign && campaigns.length > 0) {
      const updatedCampaign = campaigns.find((c) => c._id === processingCampaign._id)
      if (updatedCampaign && updatedCampaign !== processingCampaign) {
        setProcessingCampaign((prev) => ({
          ...prev,
          ...updatedCampaign,
        }))
      }
    }
  }, [campaigns, processingCampaign, showProcessTimeline])

  // New useEffect for general polling (every 4 seconds)
  useEffect(() => {
    // Clear any existing general polling interval
    if (generalPollingIntervalRef.current) {
      clearInterval(generalPollingIntervalRef.current)
      generalPollingIntervalRef.current = null
    }

    // Set up new general polling interval
    generalPollingIntervalRef.current = setInterval(() => {
      if(statusFilter!=="finished"){
         silentlyRefreshCampaigns()
      fetchCampaignStats()
      }
     
    }, 4000) // Every 4 seconds

    // Clean up interval on unmount
    return () => {
      if (generalPollingIntervalRef.current) {
        clearInterval(generalPollingIntervalRef.current)
        generalPollingIntervalRef.current = null
      }
    }
  }, [silentlyRefreshCampaigns, fetchCampaignStats])

  // Add this effect to check for running campaigns and set up polling
  useEffect(() => {
    // Check if any campaigns are currently running
    const running = campaigns.some((campaign) => campaign.isRunning)

    setHasRunningCampaigns(running)

    // Clear any existing interval
    if (runningCampaignsIntervalRef.current) {
      clearInterval(runningCampaignsIntervalRef.current)
      runningCampaignsIntervalRef.current = null
    }

    // Only set up auto-refresh if we have running campaigns and we're on the processing tab
    if (running && statusFilter === "processing") {
      runningCampaignsIntervalRef.current = setInterval(() => {
        silentlyRefreshCampaigns()
      }, 10000) // Check every 10 seconds
    }

    // Clean up interval on unmount or when dependencies change
    return () => {
      if (runningCampaignsIntervalRef.current) {
        clearInterval(runningCampaignsIntervalRef.current)
        runningCampaignsIntervalRef.current = null
      }
    }
  }, [campaigns, statusFilter, silentlyRefreshCampaigns])

  // Check if any campaigns are being filtered and set up auto-refresh
  useEffect(() => {
    // Check if any campaigns are currently being filtered
    const filtering = campaigns.some(
      (campaign) =>
        campaign.filteringStatus === "step1_in_progress" ||
        campaign.filteringStatus === "step2_in_progress" ||
        campaign.filteringStatus === "not_started", // Also refresh for campaigns that haven't started filtering yet
    )

    setHasFilteringInProgress(filtering)

    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }

    // Only set up auto-refresh if we have campaigns being filtered and we're on the pending tab
    if (filtering && statusFilter === "pending") {
      refreshIntervalRef.current = setInterval(() => {
        silentlyRefreshCampaigns()
      }, 7000) // Increased from 5000 to 7000 ms
    }

    // Clean up interval on unmount or when dependencies change
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [campaigns, statusFilter, silentlyRefreshCampaigns])

  // New function to fetch available servers
  const fetchServers = useCallback(async () => {
    setLoadingServers(true)
    try {
      const response = await fetch(`${API_URL}/api/campaigns/filter/servers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch servers")
      }

      const data = await response.json()
      const serversList = data.servers || []
      setServers(serversList)

      // Set the first server as default if servers are available
      if (serversList.length > 0) {
        setSelectedServerId(serversList[0]._id)
        setFindNonWhatsAppServerId(serversList[0]._id)
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setLoadingServers(false)
    }
  }, [userInfo.token])

  const handlePageChange = (event, value) => {
    setPage(value)
  }

  const handleStatusFilterChange = (event, newValue) => {
    if (newValue !== null) {
      setDataStatusKey(null)
      setStatusFilter(newValue)
      setPage(1)
    }
  }

  const handleOpenMenu = (event, campaignId) => {
    setAnchorEl(event.currentTarget)
    setSelectedCampaignId(campaignId)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedCampaignId(null)
  }

  const handleViewCampaign = async (campaignId) => {
    handleCloseMenu()
    setopeningModal(true)
    try {
      const result = await dispatch(getCampaignById(campaignId)).unwrap()
      setSelectedCampaign(result.campaign)
      setProcessingCampaign(result.campaign)
      setShowProcessTimeline(true)
      setViewModeOnly(true)
    } catch (error) {
      toast.error("Failed to fetch campaign details")
    }
    setopeningModal(false)
  }

  const handleOpenEditDialog = (campaignId, type = "view") => {
    setopeningModal(true)
    handleCloseMenu()
    dispatch(getCampaignById(campaignId))
      .unwrap()
      .then((result) => {
        const campaignData = result.campaign
        setSelectedCampaign(campaignData)
        setopeningModal(false)
        if (type === "view") {
        } else {
          setEditFormData({
            title: campaignData.title || "",
            message: campaignData.message || "",
            buttons: campaignData.buttons || [],
            numbers: campaignData.numbers || [],
          })
        }

        setMedia(campaignData.media || [])
        setEditDialogOpen(true)
      })
      .catch((error) => {
        toast.error("Failed to fetch campaign details for editing")
      })
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setEditFormData({
      title: "",
      message: "",
      buttons: [],
      numbers: [],
    })
    setMedia([])
  }

  const handleEditFormChange = (e) => {
    const { name, value } = e.target
    setEditFormData({
      ...editFormData,
      [name]: value,
    })
  }

  const handleButtonChange = (index, field, value) => {
    const updatedButtons = [...editFormData.buttons]
    updatedButtons[index] = {
      ...updatedButtons[index],
      [field]: value,
    }
    setEditFormData({
      ...editFormData,
      buttons: updatedButtons,
    })
  }

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success) {
        let type = "image"
        if (file.type.includes("video")) {
          type = "video"
        } else if (file.type.includes("pdf")) {
          type = "pdf"
        }

        const fileObj = {
          name: file.name,
          url: data.filePath,
          type,
        }

        // Append to existing media array instead of replacing it
        setMedia([...media, fileObj])
        // toast.success(`Media uploaded successfully`)
      } else {
        throw new Error(data.message || "Failed to upload file")
      }
    } catch (error) {
      toast.error(`Failed to upload media: ${error.message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveMedia = (index) => {
    setMedia(media.filter((_, i) => i !== index))
  }

  const handleSubmitEdit = () => {
    if (!selectedCampaign) return

    if ((editFormData.message || "").length > MAX_CAMPAIGN_MESSAGE_LENGTH) {
      toast.error(`Campaign message cannot exceed ${MAX_CAMPAIGN_MESSAGE_LENGTH} characters`)
      return
    }

    // Prepare data for submission
    const campaignData = {
      title: editFormData.title,
      message: editFormData.message,
      media: media.map((m) => ({ type: m.type, url: m.url, name: m.name })),
      // We don't include numbers in the update as they should be managed through filtering
    }

    // Only include buttons if it's a button campaign
    if (selectedCampaign.campaignType === "button" && editFormData.buttons.length > 0) {
      campaignData.buttons = editFormData.buttons
    }

    dispatch(updateCampaign({ id: selectedCampaign._id, campaignData }))
      .unwrap()
      .then(() => {
        //toast.success("Campaign updated successfully")
        setEditDialogOpen(false)
        fetchCampaigns()
      })
      .catch((error) => {
        toast.error(error || "Failed to update campaign")
      })
  }

  const handleOpenActionDialog = (type) => {
    handleCloseMenu()
    setActionType(type)
    setActionDialogOpen(true)
  }

  const handleAction = () => {
    if (!selectedCampaignId) return

    const statusData = {
      status: actionType === "approve" ? "processing" : "rejected",
    }

    if (actionType === "reject" && rejectionReason.trim()) {
      statusData.rejectionReason = rejectionReason
    }

    dispatch(updateCampaignStatus({ id: selectedCampaignId, statusData }))
  }

  // New handlers for server selection and filtering
  const handleOpenFilterDialog = (campaignId) => {
    setSelectedCampaignId(campaignId)
    fetchServers()
    setFilterDialogOpen(true)
  }

  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false)
    // Don't reset selectedServerId here to keep the default selection
  }

  // Update the handleFilterCampaign function to include server selection
  const handleFilterCampaign = async () => {
    try {
      const body = selectedServerId ? { serverId: selectedServerId } : {}

      // Updated API endpoint
      const response = await fetch(`${API_URL}/api/campaigns/${selectedCampaignId}/filter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify(body),
      })

      const data = await response.json()

      if (response.ok) {
        // toast.success(
        //   data.batchId
        //     ? `Campaign filtering started with batch ID: ${data.batchId.substring(0, 8)}...`
        //     : `Campaign filtering started using ${servers.find((s) => s._id === selectedServerId)?.name} server`,
        // )
        fetchCampaigns()
        setFilterDialogOpen(false)
      } else {
        throw new Error(data.message || "Failed to start filtering")
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    }
  }

  // New handlers for process timeline
  // Update the handleStartProcessing function to always set activeProcessStep to 2
  const handleStartProcessing = async (campaign) => {
    // Fetch the latest campaign details to ensure the dialog has up-to-date info
    try {
      const result = await dispatch(getCampaignById(campaign._id)).unwrap()
      setProcessingCampaign(result.campaign)
      setShowProcessTimeline(true)
      // Always start at step 2 (Find non-WhatsApp) since the first two steps are already completed
      setActiveProcessStep(2)
    } catch (error) {
      toast.error("Failed to load campaign details for processing.")
    }
  }

  const handleCloseProcessing = () => {
    setShowProcessTimeline(false)
    setProcessingCampaign(null)
    setViewModeOnly(false)
  }

  // Process timeline step handlers
  // Note: First two steps are now automatic, so these handlers are for the remaining steps

  // Open server selection dialog for Find non-WhatsApp
  const handleOpenFindNonWhatsAppDialog = () => {
    if (!processingCampaign) return
    setCurrentStepAction("findNonWhatsApp")
    fetchServers()
    setFindNonWhatsAppDialogOpen(true)
  }

  const handleCloseFindNonWhatsAppDialog = () => {
    setFindNonWhatsAppDialogOpen(false)
    // Don't reset findNonWhatsAppServerId here to keep the default selection
    setCurrentStepAction("")
  }

  // Update the handleFindNonWhatsApp function to hide the Filter button after starting
  const handleFindNonWhatsApp = async () => {
    if (!processingCampaign) return

    try {
      setIsNonWhatsappFiltering(true)

      const body = findNonWhatsAppServerId ? { serverId: findNonWhatsAppServerId } : {}

      // Call the API endpoint for finding non-WhatsApp numbers
      const response = await fetch(`${API_URL}/api/campaigns/${processingCampaign._id}/find-non-whatsapp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify(body),
      })

      const data = await response.json()

      if (response.ok) {
        // toast.success(
        //   findNonWhatsAppServerId
        //     ? `Finding non-WhatsApp numbers using ${servers.find((s) => s._id === findNonWhatsAppServerId)?.name} server`
        //     : "Finding non-WhatsApp numbers with default server",
        // )
        // Close the server selection dialog
        setFindNonWhatsAppDialogOpen(false)

        // If we have a batchId, update the campaign object
        if (data.batchId) {
          setProcessingCampaign((prev) => ({
            ...prev,
            validationBatchId: data.batchId,
          }))

          // Immediately check the validation status
          const status = await checkValidationStatus(processingCampaign._id, data.batchId)
          if (status) {
            // This will trigger the useEffect in CampaignDetailsDialog to start polling
            fetchCampaigns()
          }
        }
      } else {
        throw new Error(data.message || "Failed to find non-WhatsApp numbers")
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setIsNonWhatsappFiltering(false)
    }
  }

  // Remove the handleAddToCollection and handleFilterFromCampaign functions since they're no longer needed
  // Also update the handleSkipFiltering function to complete the process
  const handleSkipFiltering = async () => {
    if (!processingCampaign) return

    try {
      // Update the campaign to mark step3 as completed when skipping
      const response = await fetch(`${API_URL}/api/campaigns/${processingCampaign._id}/skip-step3`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to skip filtering")
      }

      // Skip directly to completion
      await handleCompleteProcess()

      //toast.success("Filtering skipped, campaign ready for processing")
    } catch (error) {
      toast.error(`Failed to skip filtering: ${error.message}`)
    }
  }

  const handleCompleteProcess = async () => {
    if (!processingCampaign) return

    try {
      // Update campaign status to approved
      await dispatch(
        updateCampaignStatus({
          id: processingCampaign._id,
          statusData: { status: "processing" },
        }),
      ).unwrap()

      //toast.success("Campaign approved and moved to processing")
      setShowProcessTimeline(false)
      fetchCampaigns()
    } catch (error) {
      toast.error("Failed to complete process")
    }
  }

  // Add this function to handle resetting a campaign
  const handleResetCampaign = (campaignId, fromTab) => {
    setCampaignToReset({ _id: campaignId })
    setResetFromTab(fromTab)
    setResetConfirmDialogOpen(true)
  }

  const confirmResetCampaign = async () => {
    if (!campaignToReset || !resetFromTab) return

    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignToReset._id}/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({ fromTab: resetFromTab }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to reset campaign")
      }

      toast.success("Campaign has been reset and moved to processing") // Updated toast message
      setShowProcessTimeline(false)
      setResetConfirmDialogOpen(false) // Close the confirmation dialog

      // Switch to processing tab and refresh campaigns
      setStatusFilter("processing") // Changed to processing as per user request
      fetchCampaigns()
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setCampaignToReset(null)
      setResetFromTab("")
    }
  }

  // Update the handleRejectProcess function to switch to the rejected tab after successful rejection
  const handleRejectProcess = async () => {
    if (!processingCampaign) return

    try {
      // Update campaign status to rejected
      await dispatch(
        updateCampaignStatus({
          id: processingCampaign._id,
          statusData: { status: "rejected", rejectionReason: "Rejected during processing" },
        }),
      ).unwrap()

      toast.success("Campaign rejected")
      setShowProcessTimeline(false)

      // Switch to rejected tab and refresh campaigns
      setStatusFilter("rejected")
      fetchCampaigns()
    } catch (error) {
      toast.error("Failed to reject campaign")
    }
  }

  // Handler for opening retry dialog
  const handleOpenRetryDialog = (campaignId) => {
    setFailedCampaignId(campaignId)
    setRetryDialogOpen(true)
  }

  // Handler for closing retry dialog
  const handleCloseRetryDialog = () => {
    setRetryDialogOpen(false)
    setFailedCampaignId(null)
    setValidationError(null)
  }

  // Handler for retrying validation
  const handleRetryValidation = async () => {
    if (!failedCampaignId) return

    try {
      // Clear the validation error first
      const response = await fetch(`${API_URL}/api/campaigns/${failedCampaignId}/clear-validation-error`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to clear validation error")
      }

      // Close retry dialog and open server selection
      setRetryDialogOpen(false)
      setSelectedCampaignId(failedCampaignId)
      setCurrentStepAction("findNonWhatsApp")
      fetchServers()
      setFindNonWhatsAppDialogOpen(true)

      toast.success("Validation error cleared. Please select a server to retry.")
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    }
  }

  // Handler for skipping validation and proceeding
  const handleSkipValidation = async () => {
    if (!failedCampaignId) return

    try {
      const response = await fetch(`${API_URL}/api/campaigns/${failedCampaignId}/skip-validation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to skip validation")
      }

      //toast.success("Validation skipped. Campaign is ready for processing.")
      setRetryDialogOpen(false)
      fetchCampaigns()
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    }
  }

  // Update the checkValidationStatus function to handle validation status better
  const checkValidationStatus = useCallback(
    async (campaignId, batchId) => {
      if (!campaignId || !batchId) return null

      try {
        const response = await fetch(`${API_URL}/api/campaigns/filter/status/${batchId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userInfo.token}`,
          },
        })

        if (!response.ok) {
          throw new Error("Failed to fetch validation status")
        }

        const data = await response.json()

        // If validation is complete or has error, refresh campaigns
        if (data.status && (data.status.status === "completed" || data.status.status === "error")) {
          silentlyRefreshCampaigns()

          // If there's an error, set the validation error state
          if (data.status.status === "error") {
            setValidationError(data.status.error || "Validation failed")
          }

          // If we have the processing campaign, update it with the latest data
          if (processingCampaign && processingCampaign._id === campaignId) {
            dispatch(getCampaignById(campaignId))
              .unwrap()
              .then((result) => {
                setProcessingCampaign(result.campaign)
              })
              .catch((error) => {
                console.error("Failed to refresh campaign:", error)
              })
          }
        }
        return data
      } catch (error) {
        console.error("Error checking validation status:", error)
        setValidationError(error.message)
        return null
      }
    },
    [userInfo.token, silentlyRefreshCampaigns, dispatch, processingCampaign],
  )

  // Updated formatDate function to make it more user-readable with AM/PM
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  // Helper function to get filtering status chip
  const getFilteringStatusChip = (campaign) => {
    if (campaign.channelWaitReason) {
      return (
        <Tooltip
          title={
            campaign.filteringError ||
            "No active WhatsApp channel or demo server is available. The campaign remains pending and will retry automatically."
          }
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              icon={<HourglassEmptyIcon fontSize="small" />}
              label="NO ACTIVE CHANNEL"
              size="small"
              sx={{
                ml: 1,
                bgcolor: "#fff3e0",
                color: "#e65100",
              }}
            />
          </Box>
        </Tooltip>
      )
    }

    // Check for validation errors first
    if (campaign.validationBatchId && campaign.validationError) {
      return (
        <Tooltip title={`Validation failed: ${campaign.validationError}`}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              icon={<ErrorIcon fontSize="small" />}
              label="VALIDATION FAILED"
              size="small"
              sx={{
                ml: 1,
                bgcolor: "#ffebee",
                color: "#c62828",
                cursor: "pointer",
              }}
              onClick={() => handleOpenRetryDialog(campaign._id)}
            />
          </Box>
        </Tooltip>
      )
    }

    switch (campaign.filteringStatus) {
      case "not_started":
        return (
          <Tooltip title="Campaign is being prepared">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                icon={<HourglassEmptyIcon fontSize="small" />}
                label="GETTING READY"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                }}
              />
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </Box>
          </Tooltip>
        )
      case "step1_in_progress":
        return (
          <Tooltip title="Step 1 filtering in progress">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                icon={<FilterAltIcon fontSize="small" />}
                label="FILTERING"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                }}
              />
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </Box>
          </Tooltip>
        )
      case "step1_completed":
        return (
          <Tooltip title="Step 1 filtering completed">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                icon={<HourglassEmptyIcon fontSize="small" />}
                label="VALIDATING"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                }}
              />
              <CircularProgress size={16} />
            </Box>
          </Tooltip>
        )
      case "step2_in_progress":
        return (
          <Tooltip title="Step 2 filtering in progress">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                icon={<FilterAltIcon fontSize="small" />}
                label="VALIDATING"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                }}
              />
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </Box>
          </Tooltip>
        )
      case "step2_completed":
        return statusFilter === "finished" ? null : (
          <Tooltip title="Campaign ready for processing">
            <Chip
              icon={<CheckCircleIcon fontSize="small" />}
              label="READY"
              size="small"
              sx={{
                ml: 1,
                bgcolor: "#e8f5e9",
                color: "#2e7d32",
              }}
            />
          </Tooltip>
        )
      case "step3_in_progress":
        return (
          <Tooltip title="Step 3 filtering in progress">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Chip
                icon={<FilterAltIcon fontSize="small" />}
                label="FILTERING"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                }}
              />
              <CircularProgress size={16} sx={{ ml: 1 }} />
            </Box>
          </Tooltip>
        )
      case "step3_completed":
        return (
          <Tooltip title={statusFilter === "processing" ? "" : "Campaign ready for processing"}>
            {/* <Chip
              icon={<CheckCircleIcon fontSize="small" />}
              label="READY"
              size="small"
              sx={{
                ml: 1,
                bgcolor: "#e8f5e9",
                color: "#2e7d32",
              }}
            /> */}
          </Tooltip>
        )
      default:
        return null
    }
  }

  // New function to handle date filter changes:
  const handleDateFilterChange = (filterType, date = null) => {
    setDateFilters((prev) => ({
      ...prev,
      [statusFilter]: { type: filterType, date },
    }))

    // Reset to first page when changing filters
    setPage(1)
  }

  const handleCountryFilterChange = (value) => {
    setCountryFilter(value)
  }
  const handleReportChangedFilterChange = (value) => {
    setReportChnagedFilter(value)
  }

  // NEW: Handlers for Campaign Report Dialog
  const handleOpenReportDialog = async (campaign) => {
    handleCloseMenu()
    try {
      const result = await dispatch(getCampaignById(campaign._id)).unwrap()
      setSelectedCampaignForReport(result.campaign)
      setReportDialogOpen(true)
    } catch (error) {
      toast.error("Failed to fetch campaign details for report")
    }
  }

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false)
    setSelectedCampaignForReport(null)
  }

  // New function to get media URL
  const getMediaUrl = (media) => {
    if (!media || !media.url) return null

    // If media.url is already a full URL, return it
    if (media.url.startsWith("http://") || media.url.startsWith("https://")) {
      return media.url
    }

    // Otherwise, prepend API_URL to the relative path
    return `${API_URL}${media.url.startsWith("/") ? media.url : `/${media.url}`}`
  }

  // Handler for opening send message dialog
  const handleOpenSendMessageDialog = (campaignId) => {
    handleCloseMenu()
    const campaign = campaigns.find((c) => c._id === campaignId)
    setSelectedCampaignForSend(campaign)
    setSendMessageDialogOpen(true)
  }

  // Handler for closing send message dialog
  const handleCloseSendMessageDialog = () => {
    setSendMessageDialogOpen(false)
    setSendMessageMobile("")
    setSelectedCampaignForSend(null)
  }

  // Handler for sending message
  const handleSendMessage = async () => {
    if (!sendMessageMobile.trim()) {
      toast.error("Please enter a mobile number")
      return
    }

    if (!selectedCampaignForSend) {
      toast.error("Campaign not found")
      return
    }

    try {
      const response = await fetch(`${API_URL}/api/campaigns/${selectedCampaignForSend._id}/send-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({
          phoneNumber: sendMessageMobile.trim(),
        }),
      })

      const data = await response.json()

      if (response.ok) {
        //toast.success(`Message sent successfully to ${sendMessageMobile}`)
        handleCloseSendMessageDialog()
      } else {
        throw new Error(data.message || "Failed to send message")
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleRecordsPerPageChange = (event) => {
    setRecordsPerPage(event.target.value)
    setPage(1) // Reset to first page when changing records per page
  }

  return (
    <DashboardLayout>
      {/* Add the CSS styles */}
      <style>{tableStyles}</style>

      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Pending Campaigns
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          {" "}
          {/* reduced padding from 3 to 2 */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            {" "}
            {/* reduced margin bottom from 3 to 2 */}
            <Tabs
              value={statusFilter}
              onChange={handleStatusFilterChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                overflowX: "auto", // enable horizontal scroll if needed
                maxWidth: "100%",
                "& .MuiTab-root": {
                  textTransform: "none",
                  minWidth: 120, // reduced from 100 to 80
                  fontSize: "0.775rem", // smaller font size
                  padding: "4px 5px", // reduced padding
                },
                "& .Mui-selected": {
                  color: "#008069",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#008069",
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Chip
                      label="PENDING"
                      size="small"
                      sx={{
                        bgcolor: "#e3f2fd",
                        color: "#1976d2",
                        mr: 0.5, // reduced margin from 1 to 0.5
                        fontSize: "0.75rem", // smaller font size
                        height: "20px", // smaller height
                      }}
                    />
                    {stats?.pending || 0}
                  </Box>
                }
                value="pending"
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Chip
                      label="PROCESSING"
                      size="small"
                      sx={{
                        bgcolor: "#e8f5e9",
                        color: "#2e7d32",
                        mr: 0.5, // reduced margin from 1 to 0.5
                        fontSize: "0.75rem", // smaller font size
                        height: "20px", // smaller height
                      }}
                    />
                    {stats?.processing || 0}
                  </Box>
                }
                value="processing"
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Chip
                      label="FINISHED"
                      size="small"
                      sx={{
                        bgcolor: "#e8f5e9",
                        color: "#2e7d32",
                        mr: 0.5, // reduced margin from 1 to 0.5
                        fontSize: "0.75rem", // smaller font size
                        height: "20px", // smaller height
                      }}
                    />
                    {stats?.finished || 0}
                  </Box>
                }
                value="finished"
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Chip
                      label="REJECTED"
                      size="small"
                      sx={{
                        bgcolor: "#ffebee",
                        color: "#c62828",
                        mr: 0.5, // reduced margin from 1 to 0.5
                        fontSize: "0.75rem", // smaller font size
                        height: "20px", // smaller height
                      }}
                    />
                    {stats?.rejected || 0}
                  </Box>
                }
                value="rejected"
              />
            </Tabs>
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                flexWrap: { xs: "nowrap", sm: "wrap" }, // no wrap on mobile; wrap on larger screens
                overflowX: { xs: "auto", sm: "visible" }, // allow horizontal scroll on mobile
                WebkitOverflowScrolling: "touch", // smooth iOS scroll
                "& > *": { flexShrink: 0 }, // prevent items from squishing
              }}
            >
              {" "}
              {/* reduced gap from 1 to 0.5 */}
              {/*  Changed report fileter */}
              <FormControl size="small" sx={{ minWidth: 80 }}>
                {" "}
                {/* reduced minWidth from 100 to 80 */}
                <InputLabel id="changed-report-filter-label" sx={{ fontSize: "0.875rem" }}>
                  Changed R...
                </InputLabel>{" "}
                {/* shortened label and smaller font */}
                <Select
                  labelId="changed-report-filter-label"
                  value={reportChnagedFilter}
                  label="Changed R..."
                  onChange={(e) => {
                    const value = e.target.value
                    handleReportChangedFilterChange(value)
                  }}
                  sx={{ fontSize: "0.875rem" }} // smaller font size
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
              {/*  Domestic/ Non Domestic  filter dropdown */}
              <FormControl size="small" sx={{ minWidth: 80 }}>
                {" "}
                {/* reduced minWidth from 100 to 80 */}
                <InputLabel id="country-filter-label" sx={{ fontSize: "0.875rem" }}>
                  Country Type
                </InputLabel>{" "}
                {/* smaller font */}
                <Select
                  labelId="country-filter-label"
                  value={countryFilter}
                  label="Country Type"
                  onChange={(e) => {
                    const value = e.target.value
                    handleCountryFilterChange(value)
                  }}
                  sx={{ fontSize: "0.875rem" }} // smaller font size
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="dom">Domestic</MenuItem>
                  <MenuItem value="int">Non Domestic</MenuItem>
                </Select>
              </FormControl>
              {/* Date filter dropdown */}
              <FormControl size="small" sx={{ minWidth: 90 }}>
                {" "}
                {/* reduced minWidth from 120 to 90 */}
                <InputLabel id="date-filter-label" sx={{ fontSize: "0.875rem" }}>
                  Date
                </InputLabel>{" "}
                {/* smaller font */}
                <Select
                  labelId="date-filter-label"
                  value={dateFilters[statusFilter].type}
                  label="Date"
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === "custom") {
                      // Open date input on custom selection
                      setCalendarAnchorEl(true)
                    } else {
                      handleDateFilterChange(value)
                    }
                  }}
                  sx={{ fontSize: "0.875rem" }} // smaller font size
                >
                  <MenuItem value="all">All Dates</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="custom">Select Date</MenuItem>
                </Select>
              </FormControl>
              {/* Simple date input popover */}
              <Popover
                open={Boolean(calendarAnchorEl)}
                anchorEl={calendarAnchorEl}
                onClose={() => setCalendarAnchorEl(null)}
                anchorReference="anchorPosition"
                anchorPosition={{ top: 50, left: 1000 }}
              >
                <Box sx={{ p: 2, width: 300 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select Date
                  </Typography>
                  <TextField
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button size="small" onClick={() => setCalendarAnchorEl(null)}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        if (selectedDate) {
                          handleDateFilterChange("custom", selectedDate) // Ensure selectedDate is passed correctly
                        }
                        setCalendarAnchorEl(null) // Close the popover after applying the filter
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Popover>
              {/* Display selected date if custom filter is active */}
              {dateFilters[statusFilter].type === "custom" && dateFilters[statusFilter].date && (
                <Chip
                  label={new Date(dateFilters[statusFilter].date).toLocaleDateString()}
                  onDelete={() => handleDateFilterChange("all")}
                  size="small"
                  color="primary"
                  sx={{ fontSize: "0.75rem", height: "24px" }} // smaller font and height
                />
              )}
              {/* Display "Today" chip if today filter is active */}
              {dateFilters[statusFilter].type === "today" && (
                <Chip
                  label="Today"
                  onDelete={() => handleDateFilterChange("all")}
                  size="small"
                  color="primary"
                  sx={{ fontSize: "0.75rem", height: "24px" }} // smaller font and height
                />
              )}
              {/* <FormControl size="small" sx={{ minWidth: 90 }}>
             
            
                <InputLabel id="records-per-page-label" sx={{ fontSize: "0.875rem" }}>
                  Records
                </InputLabel>{" "}
            
                <Select
                  labelId="records-per-page-label"
                  value={recordsPerPage}
                  label="Records"
                  onChange={handleRecordsPerPageChange}
                  sx={{ fontSize: "0.875rem" }} // smaller font size
                >
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={25}>25 per page</MenuItem>
                  <MenuItem value={50}>50 per page</MenuItem>
                  <MenuItem value={100}>100 per page</MenuItem>
                </Select>
              </FormControl> */}
              {/* Search field */}
              <TextField
                placeholder="Search camp"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  width: "100px", // fixed width instead of auto
                  "& .MuiInputBase-input": { fontSize: "0.875rem" }, // smaller font size
                }}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />, // reduced margin
                }}
              />
            </Box>
          </Box>
          {/* Use the table container with stable loading state */}
          <Box className="table-container">
            <TableContainer sx={{ overflowX: { xs: "auto", md: "auto" } }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SR. NO.</TableCell>
                    <TableCell>NAME</TableCell>
                    <TableCell>CREATED BY</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell>TYPE</TableCell>
                    <TableCell>NUMBERS</TableCell>
                    <TableCell>DATE</TableCell>
                    <TableCell>OPTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody >
                  {dataStatusKey !== statusFilter ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : memoizedCampaigns.length > 0 ? (
                    memoizedCampaigns.map((campaign, index) => (
                      <TableRow
                        key={`campaign-${campaign._id}-${index}`}
                        hover
                        className={campaign.isRunning ? "running-campaign" : ""}
                        sx={{
                          backgroundColor:
                            campaign.status === "finished" &&
                              campaign.isWhitelistCampaign &&
                              campaign?.rejectionReason &&
                              campaign?.rejectionReason !== ""
                              ? "#e99b9bff"
                              : "",
                        }}
                      >
                        <TableCell >{(page - 1) * pagination.limit + index + 1}</TableCell>
                        <TableCell >
                          <Box>
                            <Typography variant="body1">{campaign.title}</Typography>
                            {/* Added country information below the title */}
                            <Typography
                              variant="caption"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "text.secondary",
                                mt: 0.5,
                              }}
                            >
                              <PublicIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                              {campaign.country} ({campaign.countryCode})
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 100 }}>
                            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                              {campaign.userId?.name || "Unknown"}
                            </Typography>
                            {campaign.userId && campaign.userId.role && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "text.secondary",
                                  mt: 0.5,
                                }}
                              >
                                <PersonIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                {campaign.userId.role.charAt(0).toUpperCase() + campaign.userId.role.slice(1)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, maxWidth: 100 }}>
                            <Chip
                              label={campaign.status.toUpperCase()}
                              size="small"
                              sx={{
                                bgcolor:
                                  campaign.status === "pending"
                                    ? "#e3f2fd"
                                    : campaign.status === "filtering"
                                      ? "#fff8e1"
                                      : campaign.status === "processing"
                                        ? "#e8f5e9"
                                        : campaign.status === "finished"
                                          ? "#e8f5e9"
                                          : "#ffebee",
                                color:
                                  campaign.status === "pending"
                                    ? "#1976d2"
                                    : campaign.status === "filtering"
                                      ? "#f57c00"
                                      : campaign.status === "processing"
                                        ? "#2e7d32"
                                        : campaign.status === "finished"
                                          ? "#2e7d32"
                                          : "#c62828",
                              }}
                            />
                            {campaign.isRunning && (
                              <Chip
                                label="RUNNING"
                                size="small"
                                sx={{
                                  bgcolor: "#008069",
                                  color: "white",
                                  animation: "pulse 2s infinite ease-in-out",
                                }}
                              />
                            )}
                            {campaign.filteringStatus === "step3_completed" &&
                              campaign.status === "processing" &&
                              campaign.comparisonStatus === "not_started" && (
                                <Tooltip title="Ready For Compare">
                                  <Chip
                                    icon={<CheckCircleIcon fontSize="small" />}
                                    label="Ready For Compare"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      bgcolor: "#fff3e0",
                                      color: "#5a7c5cff",
                                    }}
                                  />
                                </Tooltip>
                              )}

                            {campaign.filteringStatus === "step3_completed" &&
                              campaign.status === "processing" &&
                              campaign.comparisonStatus === "completed" && (
                                <Tooltip title="Compare Completed">
                                  <Chip
                                    icon={<CheckCircleIcon fontSize="small" />}
                                    label="Compare Completed"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      bgcolor: "#e8f5e9",
                                      color: "#2e7d32",
                                    }}
                                  />
                                </Tooltip>
                              )}

                            {getFilteringStatusChip(campaign)}
                            {campaign.validationBatchId && (
                              <Tooltip title={`Validation Batch: ${campaign.validationBatchId}`}>
                                <Chip
                                  label={`Batch: ${campaign.validationBatchId.substring(0, 6)}...`}
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    bgcolor: "#f3e5f5",
                                    color: "#7b1fa2",
                                  }}
                                />
                              </Tooltip>
                            )}
                            {/* {campaign.filteringError && (
                              <Tooltip title={`Error: ${campaign.filteringError}`}>
                                <IconButton size="small" color="error">
                                  <ErrorIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )} */}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 170 }}>
                          <Chip
                            label={`${campaign.campaignType.toUpperCase()} ${campaign.campaignOption.toUpperCase()}`}
                            size="small"
                            sx={{
                              fontSize: "0.65rem",
                              bgcolor: campaign.campaignType === "button" ? "#e8eaf6" : "#fff3e0",
                              color: campaign.campaignType === "button" ? "#3f51b5" : "#e65100",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{campaign.validNumbers}</Typography>
                            {campaign.removedNonWhatsappNumbers > 0 && (
                              <Typography variant="caption" color="error">
                                -{campaign.removedNonWhatsappNumbers} filtered
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {/* Updated date format with bold styling */}
                          <Typography sx={{ fontWeight: "bold" }}>{formatDate(campaign.createdAt)}</Typography>
                          {campaign.isRunning && campaign.runningAt && (
                            <Typography variant="caption" color="success.main">
                              Running since: {formatDate(campaign.runningAt)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {/* Show different buttons based on campaign status */}
                            {campaign.status === "rejected" && userInfo.user.role === "admin" ? (
                              // For rejected campaigns, show only reset button
                              <Tooltip title="Reset Campaign">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleResetCampaign(campaign._id, "rejected")}
                                  sx={{ mr: 1 }}
                                  disabled={isSubmitting}
                                >
                                  <RestartAltIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              // For non-rejected campaigns, show process and reject buttons
                              <>
                                <Tooltip
                                  title={
                                    campaign.status === "finished"
                                      ? "Campaign already finished"
                                      : campaign.filteringStatus === "step2_completed"
                                        ? "Process Campaign"
                                        : "Campaign is being prepared"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleStartProcessing(campaign)}
                                      sx={{ mr: 1 }}
                                      // Corrected disabled logic: disable if running or already finished
                                      disabled={campaign.isRunning || campaign.status === "finished"}
                                    >
                                      <PlayArrowIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                {/* Reject button - only show for non-rejected campaigns */}
                                <Tooltip
                                  title={
                                    campaign.status === "finished" ? "Campaign already finished" : "Reject Campaign"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setSelectedCampaignId(campaign._id)
                                      setActionType("reject")
                                      setActionDialogOpen(true)
                                    }}
                                    sx={{ mr: 1 }}
                                    // Corrected disabled logic: disable if running or already finished
                                    disabled={campaign.isRunning || campaign.status === "finished"}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                            {/* NEW: View Report Button for finished campaigns */}
                            {campaign.status === "finished" && (
                              <>
                                {/* Reset Campaign button for finished campaigns */}
                                {userInfo.user.role === "admin" && (
                                  <Tooltip title="Reset Campaign">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleResetCampaign(campaign._id, "finished")}
                                      sx={{ mr: 1 }}
                                      disabled={isSubmitting}
                                    >
                                      <RestartAltIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="View Report">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleOpenReportDialog(campaign)}
                                    sx={{ mr: 1 }}
                                  >
                                    <AssessmentIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                            {/* Menu button */}
                            <IconButton size="small" onClick={(e) => handleOpenMenu(e, campaign._id)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No campaigns found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Replace the existing loading overlay with this */}
            {openingModal && (
              <div className="table-loading-overlay">
                <CircularProgress />
                Loading.....
              </div>
            )}
          </Box>
          {pagination.pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination count={pagination.pages} page={page} onChange={handlePageChange} color="primary" />
            </Box>
          )}
        </Paper>

        {/* Process Timeline Dialog - Now using the imported component */}
        <CampaignDetailsDialog
          open={showProcessTimeline}
          onClose={handleCloseProcessing}
          campaign={processingCampaign}
          userInfo={userInfo} // Pass userInfo to the dialog
          fetchCampaigns={fetchCampaigns} // Pass fetchCampaigns to refresh campaigns after actions
          showProcessingTimeline={!viewModeOnly} // Crucial: Show processing section for Pending Campaigns page
          // Props for admin actions
          onOpenFindNonWhatsAppDialog={handleOpenFindNonWhatsAppDialog}
          onSkipFiltering={handleSkipFiltering}
          onComplete={handleCompleteProcess}
          onReject={handleRejectProcess}
          onResetCampaign={handleResetCampaign}
          isSubmitting={isSubmitting}
          checkValidationStatus={checkValidationStatus}
        />

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          PaperProps={{
            elevation: 3,
            sx: {
              minWidth: 180,
              borderRadius: 1,
              mt: 1,
            },
          }}
        >
          {/* <MenuItem onClick={() => handleOpenEditDialog(selectedCampaignId, "view")}> */}
          <MenuItem onClick={() => handleViewCampaign(selectedCampaignId)}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            View Details
          </MenuItem>
          {userInfo.user.role === "admin" && (
            <>
              <MenuItem onClick={() => handleOpenEditDialog(selectedCampaignId, "edit")}>
                <ListItemIcon>
                  <EditIcon fontSize="small" color="primary" />
                </ListItemIcon>
                Edit Campaign
              </MenuItem>
              <MenuItem onClick={() => handleOpenSendMessageDialog(selectedCampaignId)}>
                <ListItemIcon>
                  <SendIcon fontSize="small" color="success" />
                </ListItemIcon>
                Send Message
              </MenuItem>
            </>
          )}
          {/* Admin-only actions removed from menu as they're available as direct buttons */}
        </Menu>

        {/* Action Dialog */}
        <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{actionType === "approve" ? "Approve Campaign" : "Reject Campaign"}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {actionType === "approve"
                ? "Are you sure you want to approve and process this campaign? This will deduct credits from the user's account."
                : "Are you sure you want to reject this campaign? This will refund credits to the user's account."}
            </DialogContentText>
            {actionType === "reject" && (
              <TextField
                autoFocus
                margin="dense"
                label="Rejection Reason"
                fullWidth
                variant="outlined"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAction}
              color={actionType === "approve" ? "success" : "error"}
              variant="contained"
              disabled={actionType === "reject" && !rejectionReason.trim()}
            >
              {actionType === "approve" ? "Approve & Process" : "Reject"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Server Selection Dialog */}
        <Dialog open={filterDialogOpen} onClose={handleCloseFilterDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Select WhatsApp Server for Filtering</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Select a WhatsApp server to validate numbers in this campaign. This will check if the numbers are active
              on WhatsApp.
            </DialogContentText>

            {loadingServers ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <CircularProgress />
              </Box>
            ) : servers.length > 0 ? (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="server-select-label">WhatsApp Server</InputLabel>
                <Select
                  labelId="server-select-label"
                  value={selectedServerId}
                  onChange={(e) => setSelectedServerId(e.target.value)}
                  label="WhatsApp Server"
                >
                  {servers.map((server) => (
                    <MenuItem key={server._id} value={server._id}>
                      {server.name} - {server.description || server.url}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography color="error" sx={{ my: 2 }}>
                No active WhatsApp servers found. Please add servers in the admin panel.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFilterDialog}>Cancel</Button>
            <Button
              onClick={handleFilterCampaign}
              color="primary"
              variant="contained"
              disabled={isSubmitting || (servers.length > 0 && !selectedServerId)}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <FilterAltIcon />}
            >
              {isSubmitting ? "Starting..." : "Start Filtering"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Find Non-WhatsApp Server Selection Dialog */}
        <Dialog open={findNonWhatsAppDialogOpen} onClose={handleCloseFindNonWhatsAppDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Select WhatsApp Server for Filtering</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Select a WhatsApp server to find non-WhatsApp numbers in this campaign. This will check which numbers are
              not active on WhatsApp.
            </DialogContentText>

            {loadingServers ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <CircularProgress />
              </Box>
            ) : servers.length > 0 ? (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="find-server-select-label">WhatsApp Server</InputLabel>
                <Select
                  labelId="find-server-select-label"
                  value={findNonWhatsAppServerId}
                  onChange={(e) => setFindNonWhatsAppServerId(e.target.value)}
                  label="WhatsApp Server"
                >
                  {servers.map((server) => (
                    <MenuItem key={server._id} value={server._id}>
                      {server.name} - {server.description || server.url}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography color="error" sx={{ my: 2 }}>
                No active WhatsApp servers found. Please add servers in the admin panel.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFindNonWhatsAppDialog}>Cancel</Button>
            <Button
              onClick={handleFindNonWhatsApp}
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isNonWhatsappFiltering ? <CircularProgress size={20} /> : <FilterAltIcon />}
            >
              {isNonWhatsappFiltering ? "Filtering..." : "Filter Numbers"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Campaign Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Edit Campaign
            <IconButton
              aria-label="close"
              onClick={handleCloseEditDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              disabled={isSubmitting}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedCampaign && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {!viewModeOnly ? (
                      <TextField
                        label="Campaign Title"
                        name="title"
                        value={editFormData.title}
                        onChange={handleEditFormChange}
                        fullWidth
                        margin="normal"
                        disabled={isSubmitting || viewModeOnly}
                      />
                    ) : (
                      <>
                        <Typography variant="h4" gutterBottom>
                          {"Campaign Title"}
                        </Typography>
                        <Typography gutterBottom>{editFormData.title || "Untitled Campaign"}</Typography>
                      </>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {!viewModeOnly ? (
                      <TextField
                        label="Message"
                        name="message"
                        value={editFormData.message}
                        onChange={handleEditFormChange}
                        fullWidth
                        multiline
                        rows={6}
                        margin="normal"
                        inputProps={{ maxLength: MAX_CAMPAIGN_MESSAGE_LENGTH }}
                        helperText={`${(editFormData.message || "").length}/${MAX_CAMPAIGN_MESSAGE_LENGTH} characters`}
                        disabled={isSubmitting || viewModeOnly}
                      />
                    ) : (
                      <>
                        <Typography variant="h4" gutterBottom>
                          {"Message"}
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                          {editFormData.message}
                        </Typography>
                      </>
                    )}
                  </Grid>

                  {/* Numbers Display */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Recipient Numbers
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        maxHeight: 200,
                        overflow: "auto",
                        bgcolor: "#f5f5f5",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" component="div">
                        {editFormData.numbers.slice(0, 50).map((number, index) => (
                          <Box key={index} component="span" sx={{ display: "block" }}>
                            {number}
                          </Box>
                        ))}
                        {editFormData.numbers.length > 50 && (
                          <Box sx={{ mt: 1, fontStyle: "italic", color: "text.secondary" }}>
                            ...and {editFormData.numbers.length - 50} more
                          </Box>
                        )}
                      </Typography>
                    </Paper>
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                      <Chip label={`Total Numbers: ${editFormData.numbers.length}`} color="primary" />
                      {selectedCampaign.removedNonWhatsappNumbers > 0 && (
                        <Chip
                          label={`Removed Non-WhatsApp: ${selectedCampaign.removedNonWhatsappNumbers}`}
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Grid>

                  {/* Media Upload */}
                  {!viewModeOnly && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Media Files
                      </Typography>
                      <input
                        type="file"
                        accept="image/*,video/*,.pdf"
                        onChange={handleMediaUpload}
                        ref={mediaInputRef}
                        style={{ display: "none" }}
                        disabled={isSubmitting}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => mediaInputRef.current.click()}
                        disabled={isSubmitting}
                        fullWidth
                        startIcon={<UploadFileIcon />}
                      >
                        Upload Media
                      </Button>
                      {media.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2">Uploaded Media:</Typography>
                          <Grid container spacing={2}>
                            {media.map((item, index) => {
                              const mediaUrl = getMediaUrl(item)
                              const rawFileName = item?.name?.split("/").pop() || "";
                              let fileName;

                              try {
                                fileName = decodeURIComponent(rawFileName);
                              } catch {
                                fileName = rawFileName; // fallback if malformed
                              }
                              let fileIcon = null
                              if (item.type?.startsWith("image")) {
                                fileIcon = <ImageIcon fontSize="small" />
                              } else if (item.type?.startsWith("video")) {
                                fileIcon = <VideocamIcon fontSize="small" />
                              } else if (item.type === "application/pdf") {
                                fileIcon = <PictureAsPdfIcon fontSize="small" />
                              } else {
                                fileIcon = <DescriptionIcon fontSize="small" /> // Generic file icon
                              }

                              return (
                                <Grid item xs={12} sm={6} md={4} key={item.url}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      mt: 1,
                                      p: 1,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                                      {fileIcon}
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          ml: 1,
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {fileName}
                                      </Typography>
                                    </Box>
                                    <IconButton onClick={() => handleRemoveMedia(index)} disabled={isSubmitting}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </Grid>
                              )
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Grid>
                  )}

                  {media.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Media Previews:
                      </Typography>
                      <Grid container spacing={2}>
                        {media.map((item, index) => {
                          const mediaUrl = getMediaUrl(item)
                          const rawFileName = item?.name?.split("/").pop() || "";
                          let fileName;

                          try {
                            fileName = decodeURIComponent(rawFileName);
                          } catch {
                            fileName = rawFileName; // fallback if malformed
                          }

                          return (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 150, // Fixed height for consistent preview box size
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "1px solid #e0e0e0",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  position: "relative",
                                  bgcolor: "#f0f0f0", // Light background for empty/pdf previews
                                }}
                              >
                                {item.type?.startsWith("image") && (
                                  <img
                                    src={mediaUrl || `${process.env.REACT_APP_API_URL}/placeholder.svg`}
                                    alt={`Media Preview ${fileName}`}
                                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                    onError={(e) => {
                                      e.target.onerror = null
                                      e.target.src = `${process.env.REACT_APP_API_URL}/placeholder.svg`
                                    }}
                                  />
                                )}
                                {item.type?.startsWith("video") && (
                                  <video
                                    controls
                                    src={mediaUrl}
                                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                  >
                                    Your browser does not support the video tag.
                                  </video>
                                )}
                                {item.type === "pdf" && (
                                  <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                                    <PictureAsPdfIcon sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="body2">PDF: {fileName}</Typography>
                                    <Button
                                      variant="text"
                                      size="small"
                                      href={mediaUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{ mt: 1 }}
                                    >
                                      View PDF
                                    </Button>
                                  </Box>
                                )}
                                {/* {!item.type?.startsWith("image") &&
                                  !item.type?.startsWith("video") &&
                                  item.type !== "application/pdf" && (
                                    <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                                      <DescriptionIcon sx={{ fontSize: 48, mb: 1 }} />
                                      <Typography variant="body2">File: {fileName}</Typography>
                                    </Box>
                                  )} */}
                              </Box>
                              {/* <Typography
                                variant="caption"
                                sx={{
                                  mt: 0.5,
                                  display: "block",
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {fileName}
                              </Typography> */}
                            </Grid>
                          )
                        })}
                      </Grid>
                    </Grid>
                  )}

                  {/* Button Campaign Specific Fields */}
                  {selectedCampaign.campaignType === "button" &&
                    editFormData.buttons &&
                    editFormData.buttons.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Buttons
                        </Typography>
                        {editFormData.buttons.map((button, index) => (
                          <Box key={index} sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Button {index + 1}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label="Button Text"
                                  value={button.text || ""}
                                  onChange={(e) => handleButtonChange(index, "text", e.target.value)}
                                  fullWidth
                                  margin="normal"
                                  disabled={isSubmitting}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal" disabled={isSubmitting}>
                                  <InputLabel>Button Type</InputLabel>
                                  <Select
                                    value={button.type || "link"}
                                    onChange={(e) => handleButtonChange(index, "type", e.target.value)}
                                    label="Button Type"
                                  >
                                    <MenuItem value="link">Link</MenuItem>
                                    <MenuItem value="call">Call</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label={button.type === "call" ? "Phone Number" : "URL"}
                                  value={button.value || ""}
                                  onChange={(e) => handleButtonChange(index, "value", e.target.value)}
                                  fullWidth
                                  margin="normal"
                                  disabled={isSubmitting}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Grid>
                    )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            {!viewModeOnly && (
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmitEdit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{
                  bgcolor: "#008069",
                  "&:hover": { bgcolor: "#128C7E" },
                }}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Retry Validation Dialog */}
        <Dialog open={retryDialogOpen} onClose={handleCloseRetryDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ErrorIcon color="error" sx={{ mr: 1 }} />
              Validation Failed
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              The WhatsApp number validation process has failed. You can either retry the validation with a different
              server or skip this step to proceed with the campaign.
            </DialogContentText>

            {validationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Error Details:</strong> {validationError}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRetryValidation}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <RestartAltIcon />}
                fullWidth
              >
                {isSubmitting ? "Preparing..." : "Retry Validation"}
              </Button>

              <Button
                variant="outlined"
                color="warning"
                onClick={handleSkipValidation}
                disabled={isSubmitting}
                startIcon={<PlayArrowIcon />}
                fullWidth
              >
                Skip & Proceed
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRetryDialog} disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* NEW: Campaign Report Dialog */}
        <CampaignReportDialog
          open={reportDialogOpen}
          onClose={handleCloseReportDialog}
          campaign={selectedCampaignForReport}
          userInfo={userInfo}
        />

        {/* Send Message Dialog */}
        <Dialog open={sendMessageDialogOpen} onClose={handleCloseSendMessageDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Send Campaign Message
            <IconButton
              aria-label="close"
              onClick={handleCloseSendMessageDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedCampaignForSend && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Campaign: {selectedCampaignForSend.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                  Send this campaign message to a specific mobile number for testing.
                </Typography>

                <TextField
                  autoFocus
                  label="Mobile Number"
                  placeholder="Enter mobile number (e.g., +1234567890)"
                  value={sendMessageMobile}
                  onChange={(e) => setSendMessageMobile(e.target.value)}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  helperText="Include country code (e.g., +91 for India)"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSendMessageDialog}>Cancel</Button>
            <Button
              onClick={handleSendMessage}
              color="success"
              variant="contained"
              disabled={!sendMessageMobile.trim()}
              startIcon={<SendIcon />}
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#128C7E" },
              }}
            >
              Send Message
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Confirmation Dialog */}
        <Dialog open={resetConfirmDialogOpen} onClose={() => setResetConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Campaign Reset</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to reset this campaign? This action will move the campaign back to the "Processing"
              tab and allow it to be re-processed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetConfirmDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmResetCampaign}
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <RestartAltIcon />}
            >
              {isSubmitting ? "Resetting..." : "Confirm Reset"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}

export default PendingCampaignPage
